/**
 * Just enough CSV for the batch screens: read a file the person picked, write
 * one back after they have edited it.
 *
 * Not a library. What arrives here is a spreadsheet export of at most a few
 * thousand short rows, and the whole grammar that matters is quoting: a field
 * may be wrapped in double quotes, inside which commas and newlines are
 * ordinary characters and `""` means one quote. That is the part hand-rolled
 * splitting on commas gets wrong, and it is the part addresses and names hit.
 */

/** A header as written, folded to the field it names. */
export const columnKey = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/** Rows of cells. The first row is the header; blank trailing lines are dropped. */
export function parseCsv(text: string): string[][] {
  // A BOM survives Excel and would otherwise become part of the first header.
  const input = text.replace(/^﻿/, "");
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (quoted) {
      if (char === '"') {
        // Doubled inside quotes is a literal quote; otherwise the field ends.
        if (input[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      // A CRLF is one break, not two empty rows.
      if (char === "\r" && input[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += char;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Excel leaves a trailing newline, which is not a row of one empty field.
  return rows.filter((cells) => cells.some((value) => value !== ""));
}

/** Back to a file the API will read the same way it reads a template. */
export function toCsv(rows: string[][]): string {
  const cell = (value: string) =>
    /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return rows.map((row) => row.map(cell).join(",")).join("\n");
}

/** Which of `required` the header does not name. Compared by field, not spelling. */
export function missingColumns(header: string[], required: string[]): string[] {
  const have = new Set(header.map(columnKey));
  return required.filter((column) => !have.has(columnKey(column)));
}

/**
 * The text of a CSV somebody picked, in whichever encoding they saved it.
 *
 * Excel writes Windows-1252 unless it is told otherwise, and in that encoding
 * `Ñ` is the single byte `0xD1` — not valid UTF-8. Read as UTF-8 it becomes
 * `U+FFFD`, which is how `MUÑOZ` reached the preview as `MU�OZ` and was then
 * submitted that way. The replacement character is lossy: once it is in the
 * string the original byte is gone, so the encoding has to be settled here, on
 * the bytes, rather than repaired afterwards.
 *
 * In order: a BOM is the file saying what it is; a file that decodes as strict
 * UTF-8 is UTF-8 (which is what a file already holding `Ñ` correctly does);
 * anything else is Excel's Windows-1252, where every byte is a character and
 * nothing can fail. Two encodings are tried because two turn up — this is not
 * charset detection.
 */
export function decodeCsv(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // `TextDecoder` drops a BOM of its own accord; `parseCsv` strips one as well,
  // for text that did not come through here.
  if (view[0] === 0xef && view[1] === 0xbb && view[2] === 0xbf)
    return new TextDecoder("utf-8").decode(view);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(view);
  } catch {
    return new TextDecoder("windows-1252").decode(view);
  }
}

/**
 * A picked file as CSV text.
 *
 * `File.text()` is UTF-8 and nothing else, so every CSV read goes through here
 * instead — a second read of a file already on screen included, which has to
 * decode the same way or the rows would change under whoever is reading them.
 */
export async function readCsvFile(file: File): Promise<string> {
  return decodeCsv(await file.arrayBuffer());
}
